/**
 * @file LinkModal Component
 * @description Modal dialog for adding or editing an investment hyperlink.
 * Includes client-side URL validation and duplicate URL warning display.
 */

import { Button, Input } from '@medsuite/ui-components';
import { useState } from 'react';

import type { InvestmentLink } from '@medsuite/models';

interface LinkModalProps {
  link?: InvestmentLink | null;
  onSave: (title: string, url: string) => void;
  onClose: () => void;
  isSaving: boolean;
  duplicateWarning?: string | null;
}

/**
 * Validate that a URL starts with http:// or https://
 */
function isValidUrl(url: string): boolean {
  return url.startsWith('http://') || url.startsWith('https://');
}

export function LinkModal({ link, onSave, onClose, isSaving, duplicateWarning }: LinkModalProps) {
  const [title, setTitle] = useState(link?.title || '');
  const [url, setUrl] = useState(link?.url || '');
  const [urlError, setUrlError] = useState<string | null>(null);

  const isEditing = !!link;

  const handleSubmit = () => {
    // Validate URL
    if (!isValidUrl(url)) {
      setUrlError('URL must start with http:// or https://');
      return;
    }
    setUrlError(null);
    onSave(title.trim(), url.trim());
  };

  const handleUrlChange = (value: string) => {
    setUrl(value);
    if (urlError && isValidUrl(value)) {
      setUrlError(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {isEditing ? 'Edit Link' : 'Add Link'}
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter link title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              URL <span className="text-red-500">*</span>
            </label>
            <Input
              value={url}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder="https://example.com"
            />
            {urlError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{urlError}</p>
            )}
          </div>
          {duplicateWarning && (
            <div className="rounded-md bg-yellow-50 dark:bg-yellow-900/30 p-3">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                {duplicateWarning}
              </p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSaving || !title.trim() || !url.trim()}
          >
            {isSaving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Link'}
          </Button>
        </div>
      </div>
    </div>
  );
}
